import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Divider,
  IconButton,
  Chip,
} from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import LocalDiningRoundedIcon from "@mui/icons-material/LocalDiningRounded";
import SpaRoundedIcon from "@mui/icons-material/SpaRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { motion } from "framer-motion";
import greenImage from "../../static/media/StrategicAllies/green.webp";
import greenBanner from "../../static/media/StrategicAllies/green_banner.mp4";

export default function GreenPartner() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleSound = () => {
    if (!videoRef.current) return;
    const nextMuted = !videoRef.current.muted;
    videoRef.current.muted = nextMuted;
    videoRef.current.volume = 1;
    setMuted(nextMuted);
  };

  const services = [
    "Restaurante saludable: desayunos, almuerzos y cenas",
    "Piqueos y comida rápida saludable",
    "Catering saludable para eventos",
    "Postres saludables, enteros y por porción",
    "Planes nutricionales a domicilio",
    "Dietas personalizadas con consulta virtual",
    "Nutrición clínica y deportiva",
  ];

  const benefits = [
    "10% de descuento en toda la carta",
    "10% de descuento en Planes Nutricionales Ejecutivos a domicilio",
    "Promoción Tex Mex todos los lunes, miércoles y viernes para miembros activos",
    "Refill de bebida incluido con la compra de almuerzo o cena",
  ];

  return (
    <Box
      component="section"
      sx={{
        width: "100%",
        py: { xs: 5, md: 8 },
        bgcolor: "#ffffff",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 10% 8%, rgba(101,163,13,0.1) 0%, transparent 32%), radial-gradient(circle at 92% 12%, rgba(234,179,8,0.1) 0%, transparent 30%), radial-gradient(circle at 82% 88%, rgba(22,101,52,0.08) 0%, transparent 34%)",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          width: { xs: "92%", md: "86%" },
          maxWidth: "1240px",
          mx: "auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            position: "relative",
            borderRadius: { xs: "26px", md: "38px" },
            overflow: "hidden",
            background:
              "linear-gradient(135deg, #ffffff 0%, #f7fbf2 42%, #eef8e5 100%)",
            boxShadow:
              "0 28px 90px rgba(37,84,45,0.12), 0 0 0 1px rgba(22,101,52,0.08) inset",
            border: "1px solid rgba(22,101,52,0.1)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(120deg, rgba(255,255,255,0.86) 0%, rgba(255,255,255,0.52) 46%, rgba(236,247,223,0.46) 100%)",
              pointerEvents: "none",
            }}
          />

          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0, 1.06fr) minmax(310px, 410px)",
              },
              alignItems: "center",
              gap: { xs: 3.5, md: 3, lg: 4.5 },
              px: { xs: 2.2, sm: 3, md: 3.8, lg: 4.6 },
              py: { xs: 3, md: 4, lg: 4.8 },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack
                spacing={{ xs: 2.1, md: 2.4 }}
                sx={{
                  alignItems: { xs: "center", md: "flex-start" },
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ duration: 0.55 }}
                  style={{ width: "100%" }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: { xs: 330, md: 390 },
                      mx: { xs: "auto", md: 0 },
                      borderRadius: { xs: "22px", md: "28px" },
                      overflow: "hidden",
                      border: "1px solid rgba(22,101,52,0.12)",
                      boxShadow: "0 18px 46px rgba(22,101,52,0.14)",
                      bgcolor: "#ffffff",
                    }}
                  >
                    <Box
                      component="img"
                      src={greenImage}
                      alt="Green's Life Style"
                      sx={{
                        width: "100%",
                        display: "block",
                        aspectRatio: "1 / 1",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                </motion.div>
                <Typography
                  sx={{
                    color: "#14351f",
                    fontWeight: 800,
                    letterSpacing: -0.8,
                    fontSize: { xs: 30, md: 38 },
                    lineHeight: 1.03,
                    maxWidth: { xs: "100%", md: 680 },
                  }}
                >
                  Come Bien, Rinde Mejor
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(20,53,31,0.82)",
                    fontSize: { xs: 15.8, md: 16.5, lg: 17.2 },
                    fontWeight: 650,
                    lineHeight: 1.76,
                    maxWidth: { xs: "100%", md: 680 },
                  }}
                >
                  Green’s es el restaurante de comida saludable aliado de NovaXFit. 
                  Sabemos exactamente lo que tu cuerpo necesita después
                  del entrenamiento: proteína real, carbohidratos de calidad y
                  sabor sin culpa.
                </Typography>

                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 720,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1.1,
                    pt: 0.4,
                  }}
                >
                  {[
                    {
                      icon: <RestaurantRoundedIcon />,
                      title: "Comida real",
                      text: "Preparaciones saludables para tu día.",
                    },
                    {
                      icon: <LocalDiningRoundedIcon />,
                      title: "Planes a domicilio",
                      text: "Nutrición práctica para tu rutina.",
                    },
                  ].map((item) => (
                    <Box
                      key={item.title}
                      sx={{
                        borderRadius: "20px",
                        bgcolor: "rgba(255,255,255,0.78)",
                        border: "1px solid rgba(22,101,52,0.1)",
                        p: 1.8,
                        display: "flex",
                        gap: 1.3,
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "16px",
                          bgcolor: "#166534",
                          color: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          "& svg": {
                            fontSize: 25,
                          },
                        }}
                      >
                        {item.icon}
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            color: "#14351f",
                            fontWeight: 950,
                            fontSize: 15,
                            lineHeight: 1.15,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: "rgba(20,53,31,0.64)",
                            fontWeight: 700,
                            fontSize: 13.2,
                            lineHeight: 1.35,
                            mt: 0.3,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Typography
                  sx={{
                    color: "rgba(20,53,31,0.72)",
                    fontSize: { xs: 14.8, md: 15.5 },
                    fontWeight: 650,
                    lineHeight: 1.76,
                    maxWidth: { xs: "100%", md: 680 },
                  }}
                >
                  En Green’s creemos que los resultados en el gym empiezan en la
                  cocina. Por eso nos aliamos con NovaXFit: para que cada
                  atleta acceda a comida real, deliciosa y pensada para rendir
                  al máximo cada día. Sin ultraprocesados, sin excusas.
                </Typography>
              </Stack>
            </Box>

            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: { xs: "center", md: "flex-end" },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: { xs: 300, sm: 330, md: 360, lg: 390 },
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.7 }}
                  style={{ width: "100%" }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      borderRadius: { xs: "24px", md: "30px" },
                      overflow: "hidden",
                      background: "#ecfdf5",
                      border: "1px solid rgba(22,101,52,0.12)",
                      boxShadow:
                        "0 24px 60px rgba(22,101,52,0.18), 0 8px 30px rgba(132,204,22,0.16)",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        aspectRatio: "9 / 16",
                        maxHeight: { xs: "62vh", md: 560, lg: 600 },
                        bgcolor: "#dff5d7",
                      }}
                    >
                      <Box
                        component="video"
                        ref={videoRef}
                        autoPlay
                        muted
                        loop
                        playsInline
                        disablePictureInPicture
                        controlsList="nofullscreen noremoteplayback nodownload"
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "block",
                          objectFit: "cover",
                          backgroundColor: "#dff5d7",
                          pointerEvents: "none",
                        }}
                      >
                        <source src={greenBanner} type="video/mp4" />
                      </Box>
                    </Box>

                    <IconButton
                      onClick={toggleSound}
                      aria-label={muted ? "Activar sonido" : "Silenciar video"}
                      sx={{
                        position: "absolute",
                        right: 14,
                        bottom: 14,
                        width: 44,
                        height: 44,
                        bgcolor: "rgba(20,83,45,0.72)",
                        color: "#fff",
                        backdropFilter: "blur(8px)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        zIndex: 2,
                        "&:hover": {
                          bgcolor: "rgba(20,83,45,0.86)",
                        },
                      }}
                    >
                      {muted ? <VolumeOffRoundedIcon /> : <VolumeUpRoundedIcon />}
                    </IconButton>
                  </Box>
                </motion.div>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            mt: { xs: 2.8, md: 3.4 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            gap: { xs: 2.4, md: 3 },
          }}
        >
          <Box
            sx={{
              borderRadius: { xs: "24px", md: "30px" },
              bgcolor: "#ffffff",
              border: "1px solid rgba(22,101,52,0.1)",
              boxShadow: "0 16px 42px rgba(37,84,45,0.08)",
              px: { xs: 2.2, md: 3 },
              py: { xs: 2.4, md: 3 },
            }}
          >
            <Typography
              sx={{
                color: "#14351f",
                fontWeight: 950,
                fontSize: { xs: 22, md: 25 },
                mb: 1.8,
              }}
            >
              Lo que ofrecemos
            </Typography>

            <Stack spacing={1.15}>
              {services.map((item) => (
                <Box
                  key={item}
                  sx={{
                    display: "flex",
                    gap: 1.1,
                    alignItems: "flex-start",
                  }}
                >
                  <CheckCircleRoundedIcon
                    sx={{
                      color: "#65a30d",
                      fontSize: 21,
                      mt: "2px",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      color: "rgba(20,53,31,0.76)",
                      fontSize: { xs: 14.4, md: 15 },
                      fontWeight: 750,
                      lineHeight: 1.5,
                    }}
                  >
                    {item}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          <Box
            sx={{
              borderRadius: { xs: "24px", md: "30px" },
              bgcolor: "#16351f",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 16px 42px rgba(20,83,45,0.22)",
              px: { xs: 2.2, md: 3 },
              py: { xs: 2.4, md: 3 },
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(circle at 90% 0%, rgba(132,204,22,0.22) 0%, transparent 34%)",
                pointerEvents: "none",
              }}
            />

            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                sx={{
                  color: "#ffffff",
                  fontWeight: 950,
                  fontSize: { xs: 22, md: 25 },
                  mb: 1.8,
                }}
              >
                Beneficios NovaXFit
              </Typography>

              <Stack spacing={1.15}>
                {benefits.map((item) => (
                  <Box
                    key={item}
                    sx={{
                      display: "flex",
                      gap: 1.1,
                      alignItems: "flex-start",
                    }}
                  >
                    <CheckCircleRoundedIcon
                      sx={{
                        color: "#bef264",
                        fontSize: 21,
                        mt: "2px",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.82)",
                        fontSize: { xs: 14.4, md: 15 },
                        fontWeight: 750,
                        lineHeight: 1.5,
                      }}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            mt: { xs: 2.8, md: 3.4 },
            borderRadius: { xs: "22px", md: "28px" },
            background: "#ffffff",
            border: "1px solid rgba(22,101,52,0.1)",
            boxShadow: "0 16px 42px rgba(37,84,45,0.08)",
            px: { xs: 2.2, md: 3.2 },
            py: { xs: 2.2, md: 2.7 },
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 2, md: 3 }}
            divider={
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  display: { xs: "none", md: "block" },
                  borderColor: "rgba(22,101,52,0.12)",
                }}
              />
            }
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  color: "#14351f",
                  fontWeight: 950,
                  fontSize: { xs: 18, md: 20 },
                  mb: 1,
                }}
              >
                Términos y condiciones
              </Typography>

              <Stack spacing={0.85}>
                <Typography
                  sx={{
                    color: "rgba(20,53,31,0.72)",
                    fontSize: { xs: 14.1, md: 14.7 },
                    fontWeight: 750,
                    lineHeight: 1.55,
                  }}
                >
                  Aplica únicamente para miembros activos de NovaXFit.
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(20,53,31,0.72)",
                    fontSize: { xs: 14.1, md: 14.7 },
                    fontWeight: 750,
                    lineHeight: 1.55,
                  }}
                >
                  Los descuentos no aplican sobre promociones vigentes en el
                  local.
                </Typography>
              </Stack>
            </Box>

            <Box
              sx={{
                minWidth: { xs: "auto", md: 260 },
                display: "flex",
                alignItems: { xs: "stretch", md: "center" },
              }}
            >
              <Button
                component="a"
                href="https://www.instagram.com/greens.healthyfood"
                target="_blank"
                rel="noopener noreferrer"
                fullWidth
                startIcon={<InstagramIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  borderRadius: "14px",
                  px: 2.4,
                  py: 1.15,
                  fontSize: 14.5,
                  color: "#166534",
                  bgcolor: "rgba(101,163,13,0.1)",
                  border: "1px solid rgba(101,163,13,0.16)",
                  "&:hover": {
                    bgcolor: "rgba(101,163,13,0.16)",
                  },
                }}
              >
                Instagram
              </Button>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}